import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount, useWriteContract, useChainId, useSwitchChain } from 'wagmi';
import { parseUnits } from 'viem';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Lock,
  Unlock,
  TrendingUp,
  CheckCircle2,
  Wallet,
  Shield,
  Sparkles,
  ArrowRight,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { ConnectWalletButton } from '../components/wallet/ConnectWalletButton';
import { cn } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLYGON_CHAIN_ID = 137;

// ERC20 ABI (transfer only)
const ERC20_ABI = [
  {
    name: 'transfer',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'to', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [{ type: 'bool' }],
  },
];

const COLOR_MAP = {
  amber: {
    border: 'border-amber-500/30',
    glow: 'shadow-amber-500/20',
    text: 'text-amber-400',
    bg: 'bg-amber-500/10',
    button: 'from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500',
    icon: Unlock,
  },
  blue: {
    border: 'border-blue-500/30',
    glow: 'shadow-blue-500/20',
    text: 'text-blue-400',
    bg: 'bg-blue-500/10',
    button: 'from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500',
    icon: Lock,
  },
  emerald: {
    border: 'border-emerald-500/30',
    glow: 'shadow-emerald-500/20',
    text: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    button: 'from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500',
    icon: Sparkles,
  },
};

export const Packages = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  const { writeContractAsync } = useWriteContract();

  const [packages, setPackages] = useState([]);
  const [treasury, setTreasury] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USDC');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pkgsRes, treasuryRes] = await Promise.all([
          axios.get(`${API_URL}/api/investments/packages`),
          axios.get(`${API_URL}/api/investments/treasury-info`),
        ]);
        setPackages(pkgsRes.data);
        setTreasury(treasuryRes.data);
      } catch (e) {
        console.error(e);
        toast.error('Error cargando paquetes');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleInvest = async (pkg) => {
    if (!isAuthenticated) {
      toast.error('Inicia sesion para invertir');
      navigate('/login');
      return;
    }
    if (!isConnected) {
      toast.error('Conecta tu wallet primero');
      return;
    }
    if (chainId !== POLYGON_CHAIN_ID) {
      toast.error('Cambia tu wallet a la red Polygon');
      switchChain({ chainId: POLYGON_CHAIN_ID });
      return;
    }
    setSelectedPkg(pkg);
    setAmount(String(pkg.min_amount));
  };

  const submitInvestment = async () => {
    if (!selectedPkg || !amount) return;
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      toast.error('Monto invalido');
      return;
    }

    setSubmitting(true);
    let txHash = null;

    try {
      // Intentar transferencia real (si treasury es valida 0x...). Si falla o es mock dEaD,
      // se hace solo registro en backend (mock deposit).
      const treasuryAddr = treasury.treasury_address;
      const isMockTreasury =
        treasuryAddr.toLowerCase() === '0x000000000000000000000000000000000000dead';

      if (!isMockTreasury) {
        const tokenInfo = treasury.tokens[currency];
        const amountWei = parseUnits(amount, tokenInfo.decimals);
        txHash = await writeContractAsync({
          address: tokenInfo.address,
          abi: ERC20_ABI,
          functionName: 'transfer',
          args: [treasuryAddr, amountWei],
        });
        toast.success(`Transferencia enviada: ${txHash.slice(0, 10)}...`);
      } else {
        // Mock mode (treasury aun no configurado)
        toast.info('Modo simulado: registrando deposito sin transaccion real');
      }

      // Registrar en backend
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/investments`,
        {
          package_id: selectedPkg.id,
          amount: numAmount,
          currency,
          wallet_address: address,
          tx_hash: txHash,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(
        `Inversion confirmada: ${selectedPkg.name} - $${numAmount.toLocaleString()}`
      );
      navigate('/my-investments');
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.detail || e.shortMessage || e.message || 'Error en la inversion';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="packages-page">
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 mb-4">
              <TrendingUp className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-xs font-medium text-amber-400">
                Inversion en USDC/USDT • Red Polygon
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
              Paquetes de{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-amber-300 to-amber-500">
                Inversion
              </span>
            </h1>
            <p className="text-white/60 mt-2 max-w-xl">
              Deposita en stablecoins, obten rendimientos mensuales y reclama o capitaliza tu yield el dia 1 de cada mes.
            </p>
          </div>
          <ConnectWalletButton />
        </div>

        {/* Packages Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {packages.map((pkg) => {
            const colors = COLOR_MAP[pkg.color] || COLOR_MAP.amber;
            const Icon = colors.icon;
            const isSelected = selectedPkg?.id === pkg.id;
            return (
              <Card
                key={pkg.id}
                data-testid={`package-card-${pkg.id}`}
                className={cn(
                  'relative overflow-hidden transition-all duration-300 group',
                  'bg-gradient-to-br from-zinc-900/80 to-black border',
                  colors.border,
                  isSelected && 'ring-2 ring-amber-500/50',
                  'hover:shadow-2xl',
                  colors.glow
                )}
              >
                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-current to-transparent opacity-5 rounded-full blur-3xl group-hover:opacity-10 transition-opacity" />

                <CardHeader className="relative">
                  <div className="flex items-center justify-between mb-2">
                    <div className={cn('w-11 h-11 rounded-xl flex items-center justify-center', colors.bg)}>
                      <Icon className={cn('w-5 h-5', colors.text)} />
                    </div>
                    <Badge variant="outline" className={cn(colors.text, 'border-current')}>
                      {pkg.lock_months === 0 ? 'Sin bloqueo' : `${pkg.lock_months} meses`}
                    </Badge>
                  </div>
                  <CardTitle className="text-2xl">{pkg.name}</CardTitle>
                  <p className="text-sm text-white/50">{pkg.tagline}</p>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="text-center py-4">
                    <div className={cn('text-5xl font-bold tracking-tight', colors.text)}>
                      {pkg.monthly_yield_pct}%
                    </div>
                    <div className="text-xs uppercase tracking-wide text-white/40 mt-1">
                      Rendimiento Mensual
                    </div>
                  </div>

                  <ul className="space-y-2.5">
                    {pkg.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-white/70">
                        <CheckCircle2 className={cn('w-4 h-4 flex-shrink-0 mt-0.5', colors.text)} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="pt-3 border-t border-white/10 text-xs text-white/40">
                    Min. inversion:{' '}
                    <span className="font-mono text-white/70">${pkg.min_amount.toLocaleString()}</span>
                  </div>

                  <Button
                    data-testid={`invest-btn-${pkg.id}`}
                    onClick={() => handleInvest(pkg)}
                    className={cn(
                      'w-full bg-gradient-to-r text-black font-bold shadow-lg',
                      colors.button
                    )}
                  >
                    Invertir Ahora
                    <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Investment Form (modal-like inline) */}
        {selectedPkg && (
          <Card
            data-testid="investment-form"
            className="border-amber-500/30 bg-zinc-900/80 max-w-2xl mx-auto"
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-amber-400" />
                Confirmar Inversion: {selectedPkg.name}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!isConnected && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-2 text-sm text-red-300">
                  <AlertCircle className="w-4 h-4" />
                  Conecta tu wallet para continuar
                </div>
              )}
              {isConnected && chainId !== POLYGON_CHAIN_ID && (
                <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/30 flex items-center justify-between text-sm">
                  <span className="text-orange-300 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Red incorrecta
                  </span>
                  <Button
                    data-testid="form-switch-polygon-btn"
                    size="sm"
                    onClick={() => switchChain({ chainId: POLYGON_CHAIN_ID })}
                  >
                    Cambiar a Polygon
                  </Button>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-white/70 mb-1.5 block">
                  Monto a Invertir (USD)
                </label>
                <Input
                  data-testid="amount-input"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  min={selectedPkg.min_amount}
                  step="100"
                  className="bg-black/40 border-amber-500/20 font-mono text-lg"
                />
                <p className="text-xs text-white/40 mt-1">
                  Min: ${selectedPkg.min_amount.toLocaleString()}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-white/70 mb-1.5 block">Moneda</label>
                <div className="flex gap-2">
                  {['USDC', 'USDT'].map((c) => (
                    <Button
                      key={c}
                      data-testid={`currency-${c.toLowerCase()}-btn`}
                      type="button"
                      variant={currency === c ? 'default' : 'outline'}
                      onClick={() => setCurrency(c)}
                      className={cn(
                        currency === c
                          ? 'bg-amber-500 text-black hover:bg-amber-400'
                          : 'border-amber-500/20 text-white/70'
                      )}
                    >
                      {c}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 text-xs space-y-1">
                <div className="flex justify-between">
                  <span className="text-white/50">Yield Mensual:</span>
                  <span className="text-amber-400 font-mono">
                    +${((parseFloat(amount) || 0) * (selectedPkg.monthly_yield_pct / 100)).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">Yield Anual:</span>
                  <span className="text-emerald-400 font-mono">
                    +${((parseFloat(amount) || 0) * (selectedPkg.monthly_yield_pct / 100) * 12).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">Treasury:</span>
                  <span className="font-mono text-white/70 truncate ml-2">
                    {treasury?.treasury_address.slice(0, 10)}...
                  </span>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  data-testid="cancel-investment-btn"
                  variant="outline"
                  className="flex-1 border-zinc-700"
                  onClick={() => setSelectedPkg(null)}
                  disabled={submitting}
                >
                  Cancelar
                </Button>
                <Button
                  data-testid="confirm-investment-btn"
                  className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold hover:from-amber-400 hover:to-amber-500"
                  onClick={submitInvestment}
                  disabled={submitting || !isConnected || chainId !== POLYGON_CHAIN_ID}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>Confirmar Inversion</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trust signals */}
        <div className="mt-16 grid md:grid-cols-3 gap-4">
          <div className="p-5 rounded-xl bg-zinc-900/60 border border-amber-500/10">
            <Shield className="w-6 h-6 text-amber-400 mb-2" />
            <h3 className="font-semibold mb-1">Custodia Web3</h3>
            <p className="text-sm text-white/50">
              Depositas via MetaMask. Sin custodios centralizados.
            </p>
          </div>
          <div className="p-5 rounded-xl bg-zinc-900/60 border border-amber-500/10">
            <TrendingUp className="w-6 h-6 text-amber-400 mb-2" />
            <h3 className="font-semibold mb-1">Yield Automatico</h3>
            <p className="text-sm text-white/50">
              Acreditacion el dia 1 de cada mes. Reclama o capitaliza.
            </p>
          </div>
          <div className="p-5 rounded-xl bg-zinc-900/60 border border-amber-500/10">
            <Wallet className="w-6 h-6 text-amber-400 mb-2" />
            <h3 className="font-semibold mb-1">USDC y USDT</h3>
            <p className="text-sm text-white/50">
              Stablecoins en red Polygon. Bajo costo de transaccion.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Packages;
